/**
 * Handing a file to the person using the app.
 *
 * Everywhere but one place, `<a download>` is the whole story: the browser
 * takes the blob and writes it. The published demo is the one place. It runs
 * inside claude.ai's artifact viewer, in a sandboxed frame that is never
 * granted download permission, so the anchor click does nothing at all — no
 * file, no error, no notice. Every EXPORT and DOWNLOAD button on
 * seventy-odd screens was a button that appeared to work and did not.
 *
 * The viewer's own route for this is the `downloads` capability: the page
 * asks, the viewer sees what they are about to save and accepts or declines.
 * So this module tries that first and falls back to the anchor, which means
 * one code path for the browser, the platform deployment and the demo alike.
 *
 * Two things it is careful about.
 *
 * A decline is not a failure. The viewer was asked and said no; they know
 * what happened, and a page that then pops an error is arguing with them.
 * Nothing is shown.
 *
 * A save that cannot happen says so. Silence is the failure this file
 * exists to end, so when the capability is absent AND the fallback cannot
 * work either — which is exactly the sandboxed frame — the person is told,
 * rather than left clicking a button that does nothing.
 */
import { t } from "../i18n";

export type SaveOutcome = "saved" | "declined" | "unavailable" | "failed";

/** What the viewer's capability accepts, per the artifact runtime contract. */
interface Downloads {
  save(req: { filename: string; data: string | Blob }): Promise<{ status: string }>;
}

declare global {
  interface Window {
    claude?: { use?: (name: string) => Promise<unknown> };
  }
}

/**
 * The capability, asked for once.
 *
 * `use()` is memoized by the platform, but it can take up to ten seconds to
 * resolve `null` where no viewer answers, and this is called from click
 * handlers. Holding the promise ourselves means the first export pays that
 * wait and no later one does.
 */
let asked: Promise<Downloads | null> | null = null;

function downloads(): Promise<Downloads | null> {
  if (asked) return asked;
  const use = typeof window !== "undefined" ? window.claude?.use : undefined;
  asked = use
    ? Promise.resolve(use("downloads"))
        .then((d) => (d as Downloads | null) ?? null)
        .catch(() => null)
    : Promise.resolve(null);
  return asked;
}

/**
 * Whether a plain anchor can be expected to work.
 *
 * `window.claude` exists only inside claude.ai — framed by the viewer, or
 * served top-level on the artifact's own host. Anywhere else (a browser, the
 * platform's own deployment, a file opened from disk) the anchor is the
 * normal, working route. This is the one distinction that decides whether a
 * failed save is worth a notice.
 */
function insideTheViewer(): boolean {
  return typeof window !== "undefined" && typeof window.claude?.use === "function";
}

function viaAnchor(filename: string, data: Blob): void {
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

/**
 * Offer a file, by whichever route this copy of the app has.
 *
 * `data` is a Blob or a string. The viewer's capability takes a Blob without
 * reading it, which matters for the larger exports, and takes the MIME type
 * from the filename's extension rather than from the blob — so the extension
 * has to be right, and the allowed set is the viewer's, not ours: csv, json,
 * txt, md, pdf, png, jpg, svg, html, zip, xlsx and a few more.
 */
export async function saveFile(filename: string, data: Blob | string, type?: string): Promise<SaveOutcome> {
  const blob = typeof data === "string" ? new Blob([data], { type: type ?? "text/plain;charset=utf-8" }) : data;

  const capability = await downloads();
  if (capability) {
    try {
      await capability.save({ filename, data: blob });
      return "saved";
    } catch (error) {
      const code = (error as { code?: string } | null)?.code;
      if (code === "declined") return "declined";
      /* every other code means this save cannot happen; the anchor will not
         help inside the viewer, so say so rather than appearing to succeed */
      notice(whyNot(code, filename));
      return code === "unavailable" || code === "not_granted" ? "unavailable" : "failed";
    }
  }

  if (insideTheViewer()) {
    notice(
      t(
        "This copy of the platform cannot save files, so {filename} was not downloaded. Open the platform itself, or ask for the file from the desk it came from.",
        { filename },
      ),
    );
    return "unavailable";
  }

  viaAnchor(filename, blob);
  return "saved";
}

function whyNot(code: string | undefined, filename: string): string {
  switch (code) {
    case "rejected_extension":
    case "extension_not_enabled":
      return t("{filename} is not a kind of file this viewer will save.", { filename });
    case "too_large":
      return t("{filename} is too large for this viewer to save.", { filename });
    case "rate_limited":
      return t("Another download is already waiting for an answer. Finish that one first.");
    default:
      return t("{filename} could not be saved here.", { filename });
  }
}

/**
 * A short notice, in plain DOM.
 *
 * Deliberately not a React component: the callers include a machine-copied
 * board that knows nothing about React, and a notice that only some of the
 * app can show is worse than none.
 */
function notice(text: string): void {
  if (typeof document === "undefined") return;
  const id = "sky-save-notice";
  document.getElementById(id)?.remove();
  const box = document.createElement("div");
  box.id = id;
  box.setAttribute("role", "status");
  box.textContent = text;
  box.style.cssText = [
    "position:fixed",
    "inset-block-end:1.25rem",
    "inset-inline-start:50%",
    "transform:translateX(-50%)",
    "z-index:9999",
    "max-inline-size:min(38rem, calc(100vw - 2rem))",
    "padding:0.75rem 1rem",
    "border-radius:0.5rem",
    "background:#241f1c",
    "color:#f5f1ec",
    "font:500 0.875rem/1.5 system-ui, sans-serif",
    "box-shadow:0 8px 28px rgba(0,0,0,0.35)",
  ].join(";");
  document.body.appendChild(box);
  setTimeout(() => box.remove(), 7_000);
}

/**
 * Catch the download links this module does not own.
 *
 * The demonstration boards are machine-copied from the original application
 * and regenerated from a source that is not in this repository, so their
 * download buttons cannot be rewritten here — they build an `<a download>`
 * and click it. A capture-phase listener takes those clicks before the
 * browser acts on them and sends the file the same way as everything else.
 *
 * Only inside the viewer. In a browser the anchor already works, and
 * intercepting it would replace something reliable with something that asks.
 */
export function installDownloadBridge(): void {
  if (typeof document === "undefined" || !insideTheViewer()) return;

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target as Element | null;
      const anchor = target?.closest?.("a[download]") as HTMLAnchorElement | null;
      if (!anchor?.href) return;
      event.preventDefault();
      event.stopPropagation();
      const filename = anchor.getAttribute("download") || "download";
      void fetch(anchor.href)
        .then((r) => r.blob())
        .then((blob) => saveFile(filename, blob))
        .catch(() => notice(t("{filename} could not be read to save.", { filename })));
    },
    true,
  );
}
